<script setup lang="ts">
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Tag from "primevue/tag";
import { useConfirm } from "src/composables/useConfirm";
import { useLayout } from "src/composables/useLayout";
import { statusSeverity, formatPrice, formatType, formatDate } from "src/utils/recipe-formatters";
import type { FetchActionResponseLite } from "src/api/models/fetchActionResponseLite";
import type { NestedActionRecipeResponse } from "src/api/models/nestedActionRecipeResponse";

const props = defineProps<{
  actions: FetchActionResponseLite[];
  loading: boolean;
  disabled: boolean;
  page: number;
  totalPages: number;
}>();

const emit = defineEmits<{
  pageChange: [page: number];
  editAction: [action: NestedActionRecipeResponse];
  deleteAction: [id: string];
  toggleStatus: [id: string, currentStatus: string];
  linkArticle: [actionId: string];
}>();

const { confirm } = useConfirm();
const { tableSizeClass } = useLayout();

function onPage(event: { page: number }) {
  emit("pageChange", event.page + 1);
}

function onConfirmDelete(event: Event, id: string) {
  const { onAccept } = confirm(event);
  onAccept(() => emit("deleteAction", id));
}
</script>

<template>
  <DataTable
    :value="actions"
    :loading="loading"
    :class="`p-datatable-${tableSizeClass}`"
    class="border-round"
    data-key="id"
    striped-rows
    paginator
    :rows="25"
    :total-records="totalPages * 25"
    :first="(page - 1) * 25"
    lazy
    table-style="min-width: 60rem"
    @page="onPage"
  >
    <template #empty>
      <div class="text-center py-6 text-color-secondary">No actions found</div>
    </template>

    <Column field="name" header="Name" style="min-width: 220px">
      <template #body="{ data }">
        <span class="font-semibold select-text">{{ data.name ?? "-" }}</span>
      </template>
    </Column>

    <Column field="type" header="Type" style="min-width: 160px">
      <template #body="{ data }">
        <span class="select-text">{{ formatType(data.type) }}</span>
      </template>
    </Column>

    <Column field="price" header="Price" style="min-width: 100px">
      <template #body="{ data }">
        <span class="select-text">{{ formatPrice(data.price) }}</span>
      </template>
    </Column>

    <Column field="status" header="Status" style="width: 100px">
      <template #body="{ data }">
        <Tag
          :value="data.status ?? 'unknown'"
          :severity="statusSeverity(data.status)"
          class="cursor-pointer"
          @click.stop="data.id && !disabled && $emit('toggleStatus', data.id, data.status ?? 'active')"
        />
      </template>
    </Column>

    <Column field="created_at" header="Created" style="min-width: 140px">
      <template #body="{ data }">
        <span class="select-text text-sm">{{ formatDate(data.created_at) }}</span>
      </template>
    </Column>

    <Column header="" style="width: 120px" align-right>
      <template #body="{ data }">
        <div class="flex gap-1 justify-end">
          <Button
            icon="pi pi-box"
            text rounded size="small" severity="secondary"
            :disabled="disabled || !data.id"
            v-tooltip.top="'Add article'"
            @click="data.id && $emit('linkArticle', data.id)"
          />
          <Button
            icon="pi pi-pencil"
            text rounded size="small" severity="info"
            :disabled="disabled || !data.id"
            v-tooltip.top="'Edit'"
            @click="$emit('editAction', data)"
          />
          <Button
            icon="pi pi-trash"
            text rounded size="small" severity="danger"
            :disabled="disabled || !data.id"
            v-tooltip.top="'Delete'"
            @click="(e: Event) => data.id && onConfirmDelete(e, data.id)"
          />
        </div>
      </template>
    </Column>
  </DataTable>
</template>
